import React, { useCallback, useEffect, useRef } from "react";

interface Props {
    centerUrl: string;
    rightUrl: string;
    leftUrl: string;
    selectedShot: Point | null;
}

export default function Videos({
    centerUrl,
    rightUrl,
    leftUrl,
    selectedShot,
}: Props): JSX.Element {
    const centerVideoRef = useRef<HTMLVideoElement>(null);
    const leftVideoRef = useRef<HTMLVideoElement>(null);
    const rightVideoRef = useRef<HTMLVideoElement>(null);

    const onPlay = useCallback(() => {
        Promise.all([
            leftVideoRef.current?.play(),
            rightVideoRef.current?.play(),
        ]).catch(() => { });
    }, []);

    const onPause = useCallback(() => {
        leftVideoRef.current?.pause();
        rightVideoRef.current?.pause();
    }, []);

    const onSeeking = useCallback(() => {
        const currentTime = centerVideoRef.current?.currentTime;
        if (currentTime !== undefined && leftVideoRef.current && rightVideoRef.current) {
            leftVideoRef.current.currentTime = currentTime;
            rightVideoRef.current.currentTime = currentTime;
        }
    }, []);

    const onSwap = useCallback((e: React.MouseEvent<HTMLVideoElement>) => {
        if (!centerVideoRef.current || !leftVideoRef.current || !rightVideoRef.current) {
            return;
        }
        const clickSrc = e.currentTarget.src;
        const { currentTime } = centerVideoRef.current;
        centerVideoRef.current.pause();
        rightVideoRef.current.pause();
        leftVideoRef.current.pause();

        e.currentTarget.src = centerVideoRef.current.src;
        centerVideoRef.current.src = clickSrc;
        centerVideoRef.current.onloadeddata = () => {
            centerVideoRef.current!.currentTime = currentTime;
            onSeeking();
        };
    }, [onSeeking]);

    useEffect(() => {
        if (selectedShot
            && centerVideoRef.current && leftVideoRef.current && rightVideoRef.current) {
            centerVideoRef.current.currentTime = selectedShot.start;
            leftVideoRef.current.currentTime = selectedShot.start;
            rightVideoRef.current.currentTime = selectedShot.start;
            centerVideoRef.current.play().catch(() => {});
        }
    }, [selectedShot]);

    return (
        <div className="relative w-full">
            <video
                ref={centerVideoRef}
                src={`data/${centerUrl}`}
                controls
                onPlay={onPlay}
                onPause={onPause}
                onSeeking={onSeeking}
                className="w-full rounded-lg"
            >
                <track kind="captions" />
            </video>
            <div className="absolute top-0 w-1/4">
                <video src={`data/${leftUrl}`} ref={leftVideoRef} className="rounded-lg" onClick={onSwap} muted />
            </div>
            <div className="absolute left-full top-0 w-1/4 -translate-x-full">
                <video src={`data/${rightUrl}`} ref={rightVideoRef} className="rounded-lg" onClick={onSwap} muted />
            </div>
        </div>
    );
}
